import { useState, useMemo, useCallback } from 'react';
import { PartnerProfileResourceType, PartnerResourceTranslation } from '../../../../common/models/PartnerProfile';
import { isResourceDialogCompatible } from '../../../../common/utils/partnerResourceUtils';

export type ResourceDialogState = {
  open: boolean;
  url: string;
  title: string;
  resourceType?: PartnerProfileResourceType;
};

export type ResourceDialogNavigation = {
  canPrev: boolean;
  canNext: boolean;
  prev: () => void;
  next: () => void;
  show: boolean;
};

const isVideoResource = (resource: PartnerResourceTranslation) => (
  resource.resourceType === PartnerProfileResourceType.VIDEO
);

/**
 * Custom hook to manage video and PDF/DOC dialog state,
 * with optional prev/next navigation across dialog-openable resources.
 */
export const useResourceDialog = (resources?: PartnerResourceTranslation[]) => {
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState('');
  const [activeVideoTitle, setActiveVideoTitle] = useState('');
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [activePdfUrl, setActivePdfUrl] = useState('');
  const [activePdfTitle, setActivePdfTitle] = useState('');
  const [activePdfResourceType, setActivePdfResourceType] =
    useState<PartnerProfileResourceType | undefined>();
  const [activeResourceIndex, setActiveResourceIndex] = useState(-1);

  // Resources that can be opened in dialogs (videos, PDFs, DOCs, and internal case studies)
  const dialogResources = useMemo(
    () => (resources ?? []).filter((r) => isResourceDialogCompatible(r)),
    [resources],
  );

  const closeVideoDialog = useCallback(() => {
    setVideoDialogOpen(false);
    setActiveVideoUrl('');
    setActiveVideoTitle('');
    setActiveResourceIndex(-1);
  }, []);

  const closePdfDialog = useCallback(() => {
    setPdfDialogOpen(false);
    setActivePdfUrl('');
    setActivePdfTitle('');
    setActivePdfResourceType(undefined);
    setActiveResourceIndex(-1);
  }, []);

  const openResourceByIndex = useCallback((index: number) => {
    const resource = dialogResources[index];
    if (!resource) return;

    setActiveResourceIndex(index);

    if (isVideoResource(resource)) {
      setPdfDialogOpen(false);
      setActivePdfUrl('');
      setActivePdfTitle('');
      setActiveVideoUrl(resource.resourceLink);
      setActiveVideoTitle(resource.title);
      setVideoDialogOpen(true);
    } else {
      // All other dialog-compatible resources (PDFs, DOCs, internal case studies)
      setVideoDialogOpen(false);
      setActiveVideoUrl('');
      setActiveVideoTitle('');
      setActivePdfUrl(resource.resourceLink);
      setActivePdfTitle(resource.title);
      setActivePdfResourceType(resource.resourceType);
      setPdfDialogOpen(true);
    }
  }, [dialogResources]);

  const openVideoDialog = useCallback((
    url: string,
    title: string,
    resource?: PartnerResourceTranslation,
  ) => {
    if (resource) {
      const idx = dialogResources.findIndex((r) => r === resource);
      if (idx >= 0) {
        setActiveResourceIndex(idx);
      }
    }
    setActiveVideoUrl(url);
    setActiveVideoTitle(title);
    setVideoDialogOpen(true);
  }, [dialogResources]);

  const openPdfDialog = useCallback((
    url: string,
    title: string,
    resource?: PartnerResourceTranslation,
  ) => {
    if (resource) {
      const idx = dialogResources.findIndex((r) => r === resource);
      if (idx >= 0) {
        setActiveResourceIndex(idx);
      }
      setActivePdfResourceType(resource.resourceType);
    }
    setActivePdfUrl(url);
    setActivePdfTitle(title);
    setPdfDialogOpen(true);
  }, [dialogResources]);

  const handleDialogPrev = useCallback(() => {
    if (activeResourceIndex > 0) {
      openResourceByIndex(activeResourceIndex - 1);
    }
  }, [activeResourceIndex, openResourceByIndex]);

  const handleDialogNext = useCallback(() => {
    if (activeResourceIndex < dialogResources.length - 1) {
      openResourceByIndex(activeResourceIndex + 1);
    }
  }, [activeResourceIndex, dialogResources.length, openResourceByIndex]);

  const navigation: ResourceDialogNavigation = useMemo(() => ({
    canPrev: activeResourceIndex > 0,
    canNext: activeResourceIndex < dialogResources.length - 1,
    prev: handleDialogPrev,
    next: handleDialogNext,
    show: dialogResources.length > 1,
  }), [activeResourceIndex, dialogResources.length, handleDialogPrev, handleDialogNext]);

  const videoDialog: ResourceDialogState = useMemo(() => ({
    open: videoDialogOpen,
    url: activeVideoUrl,
    title: activeVideoTitle,
  }), [videoDialogOpen, activeVideoUrl, activeVideoTitle]);

  const pdfDialog: ResourceDialogState = useMemo(() => ({
    open: pdfDialogOpen,
    url: activePdfUrl,
    title: activePdfTitle,
    resourceType: activePdfResourceType,
  }), [pdfDialogOpen, activePdfUrl, activePdfTitle, activePdfResourceType]);

  return {
    videoDialog,
    pdfDialog,
    openVideoDialog,
    openPdfDialog,
    closeVideoDialog,
    closePdfDialog,
    openResourceByIndex,
    navigation,
    dialogResources,
  };
};
